import React from 'react';
import { kebabCase } from 'lodash';
import moment, { Moment } from 'moment';
import { useField } from 'formik';
import { DatePicker } from 'syngenta-digital-cropwise-react-ui-kit';
import { FieldLabel, FieldMessage } from './FieldChrome';
import useFieldMessage from './useFieldMessage';
import { DateFieldValue } from './types';
import './disabledFields.css';

type FormikDatePickerProps = {
  name: string;
  id?: string;
  label?: React.ReactNode;
  format?: string;
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  inputReadOnly?: boolean;
  errorMessage?: string | null;
  'data-testid'?: string;
};

const toMoment = (value: DateFieldValue) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const date = moment(value);
  return date.isValid() ? date : null;
};

// The kit keeps the previous value's time of day; a date-only field stores
// local midnight, as grow-ui's flatpickr did.
const toStoredDate = (date: Moment | null) =>
  date
    ? date
        .clone()
        .startOf('day')
        .toDate()
    : null;

const FormikDatePicker = ({
  name,
  id,
  label,
  format = 'MM/DD/YYYY',
  errorMessage,
  'data-testid': dataTestId,
  ...rest
}: FormikDatePickerProps) => {
  const [field, , { setValue, setTouched }] = useField<DateFieldValue>(name);
  const { hasError, message } = useFieldMessage(name, errorMessage);
  const inputId = id ?? kebabCase(name);

  return (
    <div className="h-8">
      {label && <FieldLabel htmlFor={inputId}>{label}</FieldLabel>}
      <DatePicker
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
        id={inputId}
        name={name}
        data-testid={dataTestId}
        className="w-full cwf-kit-date-picker"
        format={format}
        value={toMoment(field.value)}
        status={hasError ? 'error' : undefined}
        onBlur={() => setTouched(true)}
        onChange={(date: Moment | null) => {
          // Touching with validation here would validate the value from
          // before the pick, and that run could finish after the one below.
          setTouched(true, false);
          setValue(toStoredDate(date));
        }}
      />
      <FieldMessage message={message} />
    </div>
  );
};

export default FormikDatePicker;
